import { Status } from "../../sharedComponents/StatusIndicator";
import { Customer, defaultCustomer } from "../Customer/Customer";
import i18n from "../../../translations/i18n";

export interface Location {
  id: string;
  name: string;
  shortCode?: string;
  industryDescription?: string;
  address: Address;
  lineage: string;
  establishmentType?: string | { id: string; label: string };
  sicCode?: string;
  naicsCode?: string;
  ein?: string;
  locationStatus: Status;
  parentLocation?: Location;
  children: Location[];
  customer: Customer;
  viewerCanActivateLocation?: boolean;
  viewerCanAddChildLocation?: boolean;
  viewerCanArchiveLocation?: boolean;
  viewerCanInactivateLocation?: boolean;
  viewerCanManageLocation?: boolean;
  viewerCanMoveLocation?: boolean;
}

export interface LocationAttr {
  id: string;
  name: string;
  isPhysicalLocation?: boolean;
  address?: {
    address1: string;
    address2: string;
    city: string;
    subdivision: string;
    postalCode: string;
    nation: string;
  };
  customer: Customer;
  shortCode?: string;
  ein?: string;
  sicCode?: string;
  establishmentType?:
    | "NOT_A_GOVERNMENT_ENTITY"
    | "LOCAL_GOVERNMENT_ENTITY"
    | "STATE_GOVERNMENT_ENTITY";
  industryDescription?: string;
  naicsCode?: string;
  lineage?: string;
  locationStatus?: string;
  parentLocation?: {
    id: string;
    locationStatus: string;
  };
  viewerCanActivateLocation?: false;
  viewerCanAddChildLocation?: true;
  viewerCanArchiveLocation?: false;
  viewerCanInactivateLocation?: true;
  viewerCanManageLocation?: true;
  viewerCanMoveLocation?: true;
}

export interface FormLocation
  extends Omit<Location, "parentLocation" | "children" | "customer"> {
  isPhysicalLocation?: boolean;
}

export type FormLocationKeys =
  | keyof FormLocation
  | "address.address1"
  | "address.address2"
  | "address.city"
  | "address.nation"
  | "address.postalCode"
  | "address.subdivision";

export interface Address {
  address1: string;
  address2: string;
  city: string;
  nation: string | any;
  postalCode: string;
  subdivision: string | any;
}

export const getEstablishmentTypes = () => [
  {
    id: "NOT_A_GOVERNMENT_ENTITY",
    label: i18n.t("establishment_type_not_government_entity"),
  },
  {
    id: "LOCAL_GOVERNMENT_ENTITY",
    label: i18n.t("establishment_type_local_government_entity"),
  },
  {
    id: "STATE_GOVERNMENT_ENTITY",
    label: i18n.t("establishment_type_state_government_entity"),
  },
];

export const getEstablishmentType = (key?: string) => {
  return getEstablishmentTypes().filter((option) => option.id === key)[0]
    ?.label;
};

export enum locationStatusEnum {
  Active = "Active",
  Inactive = "Inactive",
  Archived = "Archived",
  Deleted = "Deleted",
}
export type LocationStatus = "Active" | "Inactive" | "Archived" | "Deleted";
export type LocationStatusFilter = {
  id: "Inactive" | "Archived";
  text: string;
};
export const getLocationStatusFilterOptions = () => {
  return [
    { id: "Inactive", label: i18n.t("locations_status_show_inactive") },
    { id: "Archived", label: i18n.t("action_show_archived") },
  ];
};

export const defaultAddress: Address = {
  address1: "",
  address2: "",
  city: "",
  nation: null,
  postalCode: "",
  subdivision: null,
};

export const defaultLocation: Location = {
  id: "",
  name: "",
  address: defaultAddress,
  parentLocation: undefined,
  children: [],
  customer: defaultCustomer,
  lineage: "",
  locationStatus: "Inactive",
};

export const lineageDelimiter: string = ">";

export const getLocationFromLocationForm = (
  formLocation: FormLocation,
  propsLocation?: Location
): Location => {
  return propsLocation
    ? { ...propsLocation, ...formLocation }
    : { ...defaultLocation, ...formLocation };
};

export const isRootLocation = (location: Location | undefined): boolean => {
  return location ? !Boolean(location.parentLocation) : false;
};

export const isLocationPhysical = (
  location: Location | LocationAttr | undefined
): boolean => {
  return Boolean(
    location &&
      (location?.address.address1 ||
        location?.address.address2 ||
        location?.address.city ||
        location?.address.subdivision ||
        location?.address.postalCode ||
        location?.address.nation)
  );
};

export const replaceLocationInLineage = (
  lineage: string | undefined,
  oldLocation: string | undefined,
  newLocation: string | undefined
): string | undefined => {
  if (lineage && oldLocation && newLocation) {
    return lineage.replace(
      `${lineageDelimiter}${oldLocation}${lineageDelimiter}`,
      `${lineageDelimiter}${newLocation}${lineageDelimiter}`
    );
  }
  return undefined;
};

export const formatForTree = (locations: any[]): Location[] => {
  locations[0].children = locations[0].children.items;
  locations[0].children.forEach((child: any) => {
    child.children = child.children.items;
  });

  return locations as Location[];
};

export interface LocationFormInput {
  id?: string;
  name?: string;
  shortCode?: string;
  industryDescription?: string;
  address?: Address;
  lineage?: string;
  establishmentType?: {
    id:
      | "NOT_A_GOVERNMENT_ENTITY"
      | "LOCAL_GOVERNMENT_ENTITY"
      | "STATE_GOVERNMENT_ENTITY";
    label: string;
  };
  sicCode?: string;
  naicsCode?: string;
  ein?: string;
  locationStatus?: Status;
  isPhysicalLocation?: boolean;
}

export interface CreateLocationInput {
  address1?: string;
  address2?: string;
  city?: string;
  country?: string;
  customerID?: string;
  ein?: string;
  establishmentType?:
    | "NOT_A_GOVERNMENT_ENTITY"
    | "LOCAL_GOVERNMENT_ENTITY"
    | "STATE_GOVERNMENT_ENTITY";
  industryDescription?: string;
  locationStatus?: string;
  naicsCode?: string;
  name: string;
  parentID?: string;
  postalCode?: string;
  shortCode?: string;
  sicCode?: string;
  stateProvince?: string;
}

export interface UpdateLocationInput {
  address1?: string;
  address2?: string;
  city?: string;
  country?: string;
  ein?: string;
  establishmentType?:
    | "NOT_A_GOVERNMENT_ENTITY"
    | "LOCAL_GOVERNMENT_ENTITY"
    | "STATE_GOVERNMENT_ENTITY";
  id: string;
  industryDescription?: string;
  locationStatus?: string;
  naicsCode?: string;
  name?: string;
  postalCode?: string;
  shortCode?: string;
  sicCode?: string;
  stateProvince?: string;
}

export const findEstablishmentType = (establismentType: string | undefined) => {
  if (establismentType) {
    return getEstablishmentTypes().find(
      (option) => option.id === establismentType
    ) as {
      id:
        | "NOT_A_GOVERNMENT_ENTITY"
        | "LOCAL_GOVERNMENT_ENTITY"
        | "STATE_GOVERNMENT_ENTITY";
      label: string;
    };
  }

  return null;
};
